/**
 * Generation Store (Zustand)
 *
 * Manages global state for AI pick generation and display.
 * - Generation state: isGenerating, isDismissed, abortController
 * - Active picks: Currently displayed picks (single source of truth)
 * - Pending picks: New picks waiting for user confirmation (background generation)
 * - Generation history: Last 25 generations persisted in localStorage (24h TTL)
 */

import { authAwareFetch } from '@contexts/authHttpUtils';
import type { ParlayScore } from '@hooks/useStrategyStats';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import {
  type GenerationHistoryResponse,
  type PersistedGenerationEntry,
  withParlaySportsbook,
} from '@parlay-picker/shared/generationHistoryTypes';
import { type LeagueKey, leagueKeyOrNull } from '@parlay-picker/shared/league';
import type { PickStreamPhase } from '@parlay-picker/shared/pickStreamEvents';
import type { AnalysisConfig } from '@views/picks/constants';
import { create } from 'zustand';

// Score data for a parlay
export interface ParlayScoreData {
  strategyScore: ParlayScore | null;
  predictionScore: ParlayScore | null;
}

// Picks data structure (used for both active and pending)
export interface PicksData {
  picks: GeneratedParlay[];
  scores: ParlayScoreData[];
  generationId: string | null;
  cached: boolean;
  statsWarning: string | null;
  lineMismatchWarning: string | null;
  leagueKey: LeagueKey | null; // League used for this generation; null when a stored one names none
  cancelled?: boolean; // True if generation was stopped mid-stream by user
}

// Collapse state for generated picks UI
export interface PicksCollapseState {
  collapsedTiers: Set<string>; // Which tier groups are collapsed
  collapsedParlays: Set<number>; // Which parlay indices are collapsed (by rank)
}

// Generation history entry (persisted in localStorage)
export interface GenerationHistory {
  id: string; // generationId
  timestamp: number; // Date.now()
  picks: PicksData; // Full picks data
  config: AnalysisConfig; // Config used for this generation
  gameIds: string[]; // Games selected (IDs as strings from Odds API)
  collapseState: PicksCollapseState; // UI state when saved
  cancelled?: boolean; // True if generation was cancelled mid-stream
  /** Server-side run produced no picks. Rendered read-only; loading it would blank the view. */
  failed?: boolean;
  failureReason?: string | null;
}

// Alias for backward compatibility
export type PendingPicks = PicksData;

import type { AnalysisMode } from '@parlay-picker/shared/analysisMode';
import type { PerModeQuotaUsage } from '@parlay-picker/shared/subscriptionTier';

// Use shared type directly — import ModeQuotaInfo and getQuotaForMode from @parlay-picker/shared/subscriptionTier
type QuotaUsage = PerModeQuotaUsage;

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface GenerationState {
  // Generation state
  isGenerating: boolean;
  isDismissed: boolean; // Modal dismissed but still generating in background
  abortController: AbortController | null;
  autoApplyPicks: boolean; // Skip confirmation, auto-replace picks when done
  generationStartTime: number | null; // Timestamp when generation started (for progress calculation)
  analysisMode: AnalysisMode; // Current analysis mode (for progress estimation)

  // Picks state
  activePicks: PicksData | null; // Currently displayed picks
  pendingPicks: PicksData | null; // New picks waiting for user to load

  // UI collapse state for generated picks (reset on new generation)
  collapseState: PicksCollapseState;

  // Generation history (persisted in localStorage, max 25 entries, 24h TTL)
  generationHistory: GenerationHistory[];

  // Streaming state (parlays arriving incrementally from SSE)
  streamingParlays: GeneratedParlay[]; // Parlays as they arrive (with scores, no tier grouping yet)
  streamingScores: ParlayScoreData[]; // Scores as they arrive (parallel array)
  currentPhase: PickStreamPhase | null; // Current backend processing phase
  phaseMessage: string | null; // Human-readable phase description
  autoGroupByTier: boolean; // Animate regroup into tiers on complete (default: true)
  generationLeagueKey: LeagueKey | null; // League used for the current/last generation; null before one
  showCancelConfirm: boolean; // Cancel confirmation modal visible
  queuePosition: number | null; // Position in semaphore queue (null = not queued)
  queueEstimatedWaitMs: number | null; // Estimated wait time while queued

  // Generation actions
  startGeneration: (mode: AnalysisMode, leagueKey: LeagueKey) => AbortController;
  finishGeneration: () => void;
  cancelGeneration: () => void;
  requestCancelGeneration: () => void; // Show cancel confirmation modal
  dismissCancelConfirm: () => void; // Hide cancel confirmation modal
  dismissModal: () => void;
  reopenModal: () => void; // Reopen modal after dismissing
  setAutoApplyPicks: (value: boolean) => void;

  // Picks actions
  setActivePicks: (picks: PicksData) => void;
  clearActivePicks: () => void;
  setPendingPicks: (picks: PicksData) => void;
  clearPendingPicks: () => void;
  applyPendingPicks: () => void; // Move pending → active

  // Collapse state actions
  toggleTierCollapse: (tierName: string) => void;
  toggleParlayCollapse: (parlayRank: number) => void;
  resetCollapseState: () => void;
  isTierCollapsed: (tierName: string) => boolean;
  isParlayCollapsed: (parlayRank: number) => boolean;

  // Generation history actions
  saveToHistory: (
    picks: PicksData,
    config: AnalysisConfig,
    gameIds: string[],
    sportsbook: string,
  ) => void;
  loadFromHistory: (id: string) => void;
  removeFromHistory: (id: string) => void;
  clearHistory: () => void;
  loadHistoryFromServer: () => Promise<void>;

  // Queue actions
  setQueuePosition: (position: number, estimatedWaitMs: number) => void;

  // Streaming actions
  addStreamingParlay: (parlay: GeneratedParlay, score?: ParlayScoreData | null) => void;
  setStreamingPhase: (phase: PickStreamPhase, message: string) => void;
  finalizeStreaming: (
    generationId: string | null,
    cached: boolean,
    statsWarning?: string | null,
    lineMismatchWarning?: string | null,
    overrideParlays?: GeneratedParlay[],
    overrideScores?: ParlayScoreData[],
  ) => void;
  clearStreaming: () => void;
  setAutoGroupByTier: (value: boolean) => void;

  // Quota state
  quotaUsage: QuotaUsage | null;
  fetchQuotaUsage: (authFetch: AuthFetch) => Promise<void>;
  decrementQuotaLocally: (mode: AnalysisMode) => void; // Optimistic decrement after successful generation

  // For checking current state in async callbacks (avoids stale closure)
  getIsDismissed: () => boolean;
  hasActivePicks: () => boolean;
  getAutoApplyPicks: () => boolean;
  getElapsedSeconds: () => number; // Get elapsed time since generation started
}

// Helper to create fresh collapse state
const createEmptyCollapseState = (): PicksCollapseState => ({
  collapsedTiers: new Set<string>(),
  collapsedParlays: new Set<number>(),
});

// LocalStorage key for generation history
const GENERATION_HISTORY_KEY = 'generationHistory';
const MAX_HISTORY_ENTRIES = 25;
const HISTORY_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

// Load generation history from localStorage (filter out stale entries)
function loadHistoryFromStorage(): GenerationHistory[] {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(GENERATION_HISTORY_KEY);
    if (!stored) return [];
    const history = JSON.parse(stored) as GenerationHistory[];
    const now = Date.now();
    // Reconstruct Sets from serialized arrays (JSON.stringify turns Sets into "{}")
    return history
      .filter((entry) => now - entry.timestamp < HISTORY_TTL_MS)
      .map((entry) => {
        const tiers = entry.collapseState?.collapsedTiers;
        const parlays = entry.collapseState?.collapsedParlays;
        return {
          ...entry,
          picks: { ...entry.picks, leagueKey: leagueKeyOrNull(entry.picks.leagueKey) },
          collapseState: {
            collapsedTiers: new Set(Array.isArray(tiers) ? tiers : []),
            collapsedParlays: new Set(Array.isArray(parlays) ? parlays : []),
          },
        };
      });
  } catch {
    return [];
  }
}

// Save generation history to localStorage
function saveHistoryToStorage(history: GenerationHistory[]): void {
  if (typeof window === 'undefined') return;
  try {
    // Convert Sets to arrays for JSON serialization (JSON.stringify(Set) → "{}")
    const serializable = history.map((entry) => ({
      ...entry,
      collapseState: {
        collapsedTiers: [...entry.collapseState.collapsedTiers],
        collapsedParlays: [...entry.collapseState.collapsedParlays],
      },
    }));
    localStorage.setItem(GENERATION_HISTORY_KEY, JSON.stringify(serializable));
  } catch {
    // Silently fail if localStorage is full
  }
}

export const useGenerationStore = create<GenerationState>((set, get) => ({
  // Initial state
  isGenerating: false,
  isDismissed: false,
  abortController: null,
  autoApplyPicks: false,
  generationStartTime: null,
  analysisMode: 'standard',
  activePicks: null,
  pendingPicks: null,
  collapseState: createEmptyCollapseState(),
  generationHistory: loadHistoryFromStorage(),
  streamingParlays: [],
  streamingScores: [],
  currentPhase: null,
  phaseMessage: null,
  autoGroupByTier: true,
  // Null before a generation; the builder then opens on preferredInSeasonLeagueKey's league.
  generationLeagueKey: null,
  showCancelConfirm: false,
  queuePosition: null,
  queueEstimatedWaitMs: null,
  quotaUsage: null,

  // Start a new generation - returns the AbortController for the fetch
  startGeneration: (mode: AnalysisMode, leagueKey: LeagueKey) => {
    const controller = new AbortController();
    set({
      isGenerating: true,
      isDismissed: false,
      abortController: controller,
      generationStartTime: Date.now(),
      analysisMode: mode,
      generationLeagueKey: leagueKey,
      pendingPicks: null, // Clear any existing pending picks
      queuePosition: null,
      queueEstimatedWaitMs: null,
      // Reset streaming state for new generation — start in streaming mode
      // immediately so the modal always uses real phase indicators (no time-based fallback)
      streamingParlays: [],
      streamingScores: [],
      currentPhase: 'fetching_data',
      phaseMessage: 'Connecting...',
    });
    return controller;
  },

  // Generation completed (success or error) - clean up state
  finishGeneration: () => {
    set({
      isGenerating: false,
      isDismissed: false,
      abortController: null,
    });
  },

  // Cancel ongoing generation — keeps already-streamed parlays
  cancelGeneration: () => {
    const { abortController, streamingParlays, streamingScores, generationLeagueKey } = get();
    if (abortController) {
      abortController.abort();
    }

    // If parlays already streamed in, finalize them as the result
    if (streamingParlays.length > 0) {
      const picksData: PicksData = {
        picks: streamingParlays,
        scores: streamingScores,
        generationId: null,
        cached: false,
        statsWarning: null,
        lineMismatchWarning: null,
        leagueKey: generationLeagueKey,
        cancelled: true,
      };
      set({
        activePicks: picksData,
        collapseState: createEmptyCollapseState(),
        streamingParlays: [],
        streamingScores: [],
        currentPhase: null,
        phaseMessage: null,
        isGenerating: false,
        isDismissed: false,
        abortController: null,
      });
    } else {
      set({
        isGenerating: false,
        isDismissed: false,
        abortController: null,
      });
    }
  },

  // Show cancel confirmation modal
  requestCancelGeneration: () => {
    set({ showCancelConfirm: true });
  },

  // Hide cancel confirmation modal
  dismissCancelConfirm: () => {
    set({ showCancelConfirm: false });
  },

  // Dismiss modal but continue generating in background
  dismissModal: () => {
    set({ isDismissed: true });
  },

  // Reopen modal after dismissing (to see progress again)
  reopenModal: () => {
    set({ isDismissed: false });
  },

  // Toggle auto-apply picks setting
  setAutoApplyPicks: (value: boolean) => {
    set({ autoApplyPicks: value });
  },

  // Set active picks (displayed immediately) - resets collapse state
  setActivePicks: (picks: PicksData) => {
    set({
      activePicks: picks,
      collapseState: createEmptyCollapseState(), // Reset on new picks
    });
  },

  // Clear active picks
  clearActivePicks: () => {
    set({ activePicks: null });
  },

  // Store pending picks (when generation completes while user browsed away)
  setPendingPicks: (picks: PicksData) => {
    set({ pendingPicks: picks });
  },

  // Clear pending picks (after user dismisses them)
  clearPendingPicks: () => {
    set({ pendingPicks: null });
  },

  // Move pending picks to active (atomic operation) - resets collapse state
  applyPendingPicks: () => {
    const { pendingPicks } = get();
    if (pendingPicks) {
      set({
        activePicks: pendingPicks,
        pendingPicks: null,
        collapseState: createEmptyCollapseState(), // Reset on new picks
      });
    }
  },

  // Toggle tier collapse state
  toggleTierCollapse: (tierName: string) => {
    set((state) => {
      const newCollapsedTiers = new Set(state.collapseState.collapsedTiers);
      if (newCollapsedTiers.has(tierName)) {
        newCollapsedTiers.delete(tierName);
      } else {
        newCollapsedTiers.add(tierName);
      }
      return {
        collapseState: {
          ...state.collapseState,
          collapsedTiers: newCollapsedTiers,
        },
      };
    });
  },

  // Toggle parlay collapse state
  toggleParlayCollapse: (parlayRank: number) => {
    set((state) => {
      const newCollapsedParlays = new Set(state.collapseState.collapsedParlays);
      if (newCollapsedParlays.has(parlayRank)) {
        newCollapsedParlays.delete(parlayRank);
      } else {
        newCollapsedParlays.add(parlayRank);
      }
      return {
        collapseState: {
          ...state.collapseState,
          collapsedParlays: newCollapsedParlays,
        },
      };
    });
  },

  // Reset all collapse state
  resetCollapseState: () => {
    set({ collapseState: createEmptyCollapseState() });
  },

  // Check if tier is collapsed
  isTierCollapsed: (tierName: string) => {
    return get().collapseState.collapsedTiers.has(tierName);
  },

  // Check if parlay is collapsed
  isParlayCollapsed: (parlayRank: number) => {
    return get().collapseState.collapsedParlays.has(parlayRank);
  },

  // Get current isDismissed value (for use in async callbacks)
  getIsDismissed: () => get().isDismissed,

  // Check if there are active picks (for use in async callbacks)
  hasActivePicks: () => get().activePicks !== null && get().activePicks!.picks.length > 0,

  // Get auto-apply setting (for use in async callbacks)
  getAutoApplyPicks: () => get().autoApplyPicks,

  // Get elapsed seconds since generation started
  getElapsedSeconds: () => {
    const { generationStartTime } = get();
    if (!generationStartTime) return 0;
    return Math.floor((Date.now() - generationStartTime) / 1000);
  },

  // Append a parlay as it arrives from the stream
  addStreamingParlay: (parlay: GeneratedParlay, score?: ParlayScoreData | null) => {
    set((state) => ({
      streamingParlays: [...state.streamingParlays, parlay],
      streamingScores: [
        ...state.streamingScores,
        score ?? { strategyScore: null, predictionScore: null },
      ],
    }));
  },

  // Update queue position (user is waiting for a generation slot)
  setQueuePosition: (position: number, estimatedWaitMs: number) => {
    set({ queuePosition: position, queueEstimatedWaitMs: estimatedWaitMs });
  },

  // Update the current backend phase (clears queue position — generation has started)
  setStreamingPhase: (phase: PickStreamPhase, message: string) => {
    set({
      currentPhase: phase,
      phaseMessage: message,
      queuePosition: null,
      queueEstimatedWaitMs: null,
    });
  },

  // Transition streaming parlays → activePicks or pendingPicks
  // Respects the same auto-apply logic as the non-streaming path
  // Optional overrideParlays/overrideScores: pass processed data directly from the
  // complete event to avoid an intermediate setState that causes a visible render flash.
  finalizeStreaming: (
    generationId: string | null,
    cached: boolean,
    statsWarning?: string | null,
    lineMismatchWarning?: string | null,
    overrideParlays?: GeneratedParlay[],
    overrideScores?: ParlayScoreData[],
  ) => {
    const parlays = overrideParlays ?? get().streamingParlays;
    const scores = overrideScores ?? get().streamingScores;
    const existingPicks = get().activePicks !== null && get().activePicks!.picks.length > 0;
    const hasNewPicks = parlays.length > 0;

    const picksData: PicksData = {
      picks: parlays,
      scores: scores,
      generationId,
      cached,
      statsWarning: statsWarning ?? null,
      lineMismatchWarning: lineMismatchWarning ?? null,
      leagueKey: get().generationLeagueKey,
    };

    // Only queue as pending when: auto-apply is OFF, there ARE existing picks, and new picks arrived.
    // In all other cases, apply immediately.
    const shouldQueue = !get().autoApplyPicks && existingPicks && hasNewPicks;

    if (shouldQueue) {
      // Queue as pending — user must confirm to replace current picks
      // Also finish generation atomically to avoid an intermediate render frame
      // where streaming is cleared but isGenerating is still true (picks flash away).
      set({
        pendingPicks: picksData,
        streamingParlays: [],
        streamingScores: [],
        currentPhase: 'finalizing',
        phaseMessage: 'Done!',
        isGenerating: false,
        isDismissed: false,
        abortController: null,
      });
    } else {
      set({
        activePicks: picksData,
        collapseState: createEmptyCollapseState(),
        streamingParlays: [],
        streamingScores: [],
        currentPhase: 'finalizing',
        phaseMessage: 'Done!',
        isGenerating: false,
        isDismissed: false,
        abortController: null,
      });
    }
  },

  // Reset all streaming state
  clearStreaming: () => {
    set({
      streamingParlays: [],
      streamingScores: [],
      currentPhase: null,
      phaseMessage: null,
    });
  },

  // Toggle auto-group-by-tier setting
  setAutoGroupByTier: (value: boolean) => {
    set({ autoGroupByTier: value });
  },

  // Fetch quota usage from the server
  fetchQuotaUsage: async (authFetch: AuthFetch) => {
    try {
      const res = await authFetch('/api/subscription/status');
      if (res.ok) {
        const data = await res.json();
        set({ quotaUsage: data.quota ?? null });
      }
    } catch {
      // Silently fail — quota display is non-critical
    }
  },

  // Optimistic decrement after a successful generation (decrements the specified mode)
  decrementQuotaLocally: (mode: AnalysisMode) => {
    const { quotaUsage } = get();
    if (quotaUsage) {
      set({
        quotaUsage: {
          ...quotaUsage,
          modes: quotaUsage.modes.map((m) =>
            m.mode === mode
              ? {
                  ...m,
                  used: m.used + 1,
                  remaining: Math.max(0, m.remaining - 1),
                }
              : m,
          ),
        },
      });
    }
  },

  // Save generation to history (called after setActivePicks)
  saveToHistory: (
    picks: PicksData,
    config: AnalysisConfig,
    gameIds: string[],
    _sportsbook: string,
  ) => {
    const { collapseState, generationHistory: history } = get();
    const newEntry: GenerationHistory = {
      id: picks.generationId || `gen-${Date.now()}`,
      timestamp: Date.now(),
      picks,
      config,
      gameIds,
      collapseState: {
        collapsedTiers: new Set(collapseState.collapsedTiers),
        collapsedParlays: new Set(collapseState.collapsedParlays),
      },
    };

    // Add to history (FIFO - keep max entries)
    // Server saves picks on the generations row — no client POST needed
    const updated = [newEntry, ...history].slice(0, MAX_HISTORY_ENTRIES);
    set({ generationHistory: updated });
    saveHistoryToStorage(updated);
  },

  // Load generation from history
  loadFromHistory: (id: string) => {
    const { generationHistory } = get();
    const entry = generationHistory.find((e) => e.id === id);
    if (!entry) return;

    // Restore picks + collapse state (defensive: older entries may have non-iterable Sets from JSON)
    const tiers = entry.collapseState?.collapsedTiers;
    const parlays = entry.collapseState?.collapsedParlays;
    set({
      activePicks: entry.picks,
      collapseState: {
        collapsedTiers: new Set(Array.isArray(tiers) ? tiers : []),
        collapsedParlays: new Set(Array.isArray(parlays) ? parlays : []),
      },
    });
  },

  // Remove a single entry from history
  removeFromHistory: (id: string) => {
    const { generationHistory: history } = get();
    const updated = history.filter((e) => e.id !== id);
    set({ generationHistory: updated });
    saveHistoryToStorage(updated);

    // Fire-and-forget sync to server
    fetch(`/api/generation-history/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    }).catch(() => {});
  },

  // Clear all history
  clearHistory: () => {
    set({ generationHistory: [] });
    if (typeof window !== 'undefined') {
      localStorage.removeItem(GENERATION_HISTORY_KEY);
    }

    // Fire-and-forget sync to server
    fetch('/api/generation-history', {
      method: 'DELETE',
      credentials: 'include',
    }).catch(() => {});
  },

  // Load generation history from server and merge with local
  loadHistoryFromServer: async () => {
    try {
      const res = await authAwareFetch('/api/generation-history');
      if (!res.ok) return;

      const data = (await res.json()) as GenerationHistoryResponse;
      if (!data.entries || data.entries.length === 0) return;

      const { generationHistory: localHistory } = get();
      const localIds = new Set(localHistory.map((e) => e.id));

      // Convert server entries not already in local to local GenerationHistory format
      const serverOnlyEntries: GenerationHistory[] = data.entries
        .filter((e: PersistedGenerationEntry) => !localIds.has(e.id))
        .map(
          (e: PersistedGenerationEntry): GenerationHistory => ({
            id: e.id,
            timestamp: e.timestamp,
            picks: {
              picks: withParlaySportsbook(e.picks ?? [], e.sportsbook),
              scores: [],
              generationId: e.id,
              cached: e.cached,
              statsWarning: null,
              lineMismatchWarning: null,
              leagueKey: leagueKeyOrNull(e.leagueKey),
              cancelled: e.status === 'cancelled',
            },
            config: { outputOptions: { analysisMode: e.analysisMode } } as AnalysisConfig,
            gameIds: e.gameIds,
            collapseState: createEmptyCollapseState(),
            cancelled: e.status === 'cancelled',
            failed: e.failed ?? false,
            failureReason: e.failureReason ?? null,
          }),
        );

      if (serverOnlyEntries.length === 0) return;

      // Merge: local + server-only, sort by timestamp desc, TTL-filter, limit 25
      const now = Date.now();
      const merged = [...localHistory, ...serverOnlyEntries]
        .filter((e) => now - e.timestamp < HISTORY_TTL_MS)
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, MAX_HISTORY_ENTRIES);

      set({ generationHistory: merged });
      saveHistoryToStorage(merged);
    } catch {
      // Non-critical — local history is the fallback
    }
  },
}));

// Selector hooks for common patterns (optional, for convenience)
export const useIsGenerating = (): boolean => useGenerationStore((s) => s.isGenerating);
export const useIsDismissed = (): boolean => useGenerationStore((s) => s.isDismissed);
export const useActivePicks = (): PicksData | null => useGenerationStore((s) => s.activePicks);
export const usePendingPicks = (): PicksData | null => useGenerationStore((s) => s.pendingPicks);
export const useCollapseState = (): PicksCollapseState =>
  useGenerationStore((s) => s.collapseState);
export const useQuotaUsage = (): QuotaUsage | null => useGenerationStore((s) => s.quotaUsage);
export const useStreamingParlays = (): GeneratedParlay[] =>
  useGenerationStore((s) => s.streamingParlays);
export const useStreamingScores = (): ParlayScoreData[] =>
  useGenerationStore((s) => s.streamingScores);
export const useCurrentPhase = (): PickStreamPhase | null =>
  useGenerationStore((s) => s.currentPhase);
export const usePhaseMessage = (): string | null => useGenerationStore((s) => s.phaseMessage);
export const useAutoGroupByTier = (): boolean => useGenerationStore((s) => s.autoGroupByTier);
